import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'

import {
  LedgerBadge,
  LedgerTable,
  type LedgerBadgeTone,
  type LedgerColumn,
} from '@/components/ui/LedgerTable'
import type { WhatsAppMessageStatus } from '@/lib/schemas/db'

export interface WhatsAppMessageRow {
  id: string
  toPhone: string
  customerName?: string
  /** Customer this message is addressed to (for linking to their page). */
  customerId?: string
  customerPublicId?: string
  /** The job this message is about (for linking to it). */
  serviceId?: string
  /** Public ID of the service (callout/session/job) this message is about. */
  servicePublicId?: string
  content: string
  status: WhatsAppMessageStatus
  /** Provider error explaining a Failed message; shown on hover of the pill. */
  failureReason?: string
  /** Display-ready sent timestamp (SA time), e.g. "2026-07-14 10:35"; empty until sent. */
  sentAtLabel?: string
  /** Timestamp used for default ordering — sent time, else last updated. */
  sortTimestamp: string
  /** Whole seconds until the grace period lapses; 0 once eligible to send. */
  graceSecondsRemaining: number
  cancellable: boolean
  /** A message to this recipient was sent in the last 24h — blocks resend. */
  recentlySent?: boolean
}

export interface WhatsAppScreenProps {
  rows: WhatsAppMessageRow[]
  /** Whether dispatch is globally on; drives the "Disabled" state + Resend. */
  sendingEnabled?: boolean
  onCancelMessage?: (id: string) => void
  onResendMessage?: (id: string) => void
}

const statusTone: Record<WhatsAppMessageStatus, LedgerBadgeTone> = {
  Queued: 'amber',
  Held: 'slate',
  Sent: 'teal',
  Cancelled: 'slate',
  Failed: 'amber',
}

/** '4m 32s' for longer grace periods, plain '45s' under a minute. */
function formatGraceCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

function ResendButton({
  id,
  sendingEnabled,
  recentlySent,
  onResendMessage,
}: {
  id: string
  sendingEnabled: boolean
  recentlySent?: boolean
  onResendMessage?: (id: string) => void
}) {
  const disabled = !sendingEnabled || Boolean(recentlySent)
  const title = !sendingEnabled
    ? 'Turn on sending to resend'
    : recentlySent
      ? 'A message was already sent to this customer in the last 24 hours'
      : undefined
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      className="rounded border border-slate-300 px-2 py-0.5 text-[12px] text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={() => onResendMessage?.(id)}
    >
      Resend
    </button>
  )
}

function StatusCell({
  row,
  sendingEnabled,
  onCancelMessage,
  onResendMessage,
}: {
  row: WhatsAppMessageRow
  sendingEnabled: boolean
  onCancelMessage?: (id: string) => void
  onResendMessage?: (id: string) => void
}) {
  // Held = composed while sending was off. It never auto-sends (the server
  // sender ignores Held), even after sending is switched back on — only a
  // manual resend re-queues it. So the badge is a fixed state, not a countdown.
  if (row.status === 'Held') {
    return (
      <div className="flex flex-col items-start gap-1">
        <LedgerBadge tone="slate">Disabled</LedgerBadge>
        <ResendButton
          id={row.id}
          sendingEnabled={sendingEnabled}
          recentlySent={row.recentlySent}
          onResendMessage={onResendMessage}
        />
      </div>
    )
  }

  if (row.status === 'Queued') {
    return (
      <div className="flex flex-col items-start gap-1">
        <LedgerBadge tone="amber">
          {row.graceSecondsRemaining > 0
            ? `Sends in ${formatGraceCountdown(row.graceSecondsRemaining)}`
            : 'Sending…'}
        </LedgerBadge>
        {row.cancellable ? (
          <button
            type="button"
            className="rounded border border-slate-300 px-2 py-0.5 text-[12px] text-slate-700 hover:bg-slate-100"
            onClick={() => onCancelMessage?.(row.id)}
          >
            Cancel
          </button>
        ) : null}
      </div>
    )
  }

  if (row.status === 'Failed' || row.status === 'Cancelled') {
    return (
      <div className="flex flex-col items-start gap-1">
        {row.status === 'Failed' ? (
          <span
            title={row.failureReason || undefined}
            className={`inline-block rounded-[5px] bg-rose-100 px-1.5 py-0.5 text-[11.5px] font-semibold text-rose-700${
              row.failureReason ? ' cursor-help' : ''
            }`}
          >
            Failed
          </span>
        ) : (
          <LedgerBadge tone="slate">Cancelled</LedgerBadge>
        )}
        <ResendButton
          id={row.id}
          sendingEnabled={sendingEnabled}
          recentlySent={row.recentlySent}
          onResendMessage={onResendMessage}
        />
      </div>
    )
  }

  return <LedgerBadge tone={statusTone[row.status]}>{row.status}</LedgerBadge>
}

/**
 * Message body cell: one elided line with an inline "…more" toggle on the same
 * row (to keep the table compact); expands to the full text, wrapping onto as
 * many lines as needed. The toggle only appears when the text is cut off.
 */
function MessageCell({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (expanded) return
    const el = ref.current
    if (!el) return
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [content, expanded])

  if (expanded) {
    return (
      <div className="whitespace-pre-wrap break-words">
        {content}{' '}
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-[12px] font-medium text-blue-700 hover:underline"
        >
          Show less
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-baseline gap-1.5">
      <span
        ref={ref}
        className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {content}
      </span>
      {truncated ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="shrink-0 text-[12px] font-medium text-blue-700 hover:underline"
        >
          …more
        </button>
      ) : null}
    </div>
  )
}

function buildColumns(opts: {
  sendingEnabled: boolean
  onCancelMessage?: (id: string) => void
  onResendMessage?: (id: string) => void
}): LedgerColumn<WhatsAppMessageRow>[] {
  return [
    {
      key: 'customer',
      label: 'Customer',
      widthClass: 'w-48',
      sortValue: (row) => (row.customerName ?? row.toPhone).toLowerCase(),
      filter: 'text',
      filterValue: (row) => `${row.customerName ?? ''} ${row.toPhone}`,
      render: (row) => (
        <div className="min-w-0">
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {row.customerName && row.customerId ? (
              <Link
                to="/customers/$id"
                params={{ id: row.customerPublicId ?? row.customerId }}
                className="font-medium text-blue-700 hover:underline"
              >
                {row.customerName}
              </Link>
            ) : (
              <span className="font-medium">{row.customerName ?? '—'}</span>
            )}
          </div>
          <span className="font-mono text-[12px] text-slate-400">
            {row.toPhone}
          </span>
        </div>
      ),
    },
    {
      key: 'service',
      label: 'Service ID',
      widthClass: 'w-28',
      sortValue: (row) => row.servicePublicId ?? '',
      filter: 'text',
      filterValue: (row) => row.servicePublicId ?? '',
      cellClass: 'whitespace-nowrap',
      render: (row) => {
        const jobParam = row.servicePublicId ?? row.serviceId
        return jobParam ? (
          <Link
            to="/jobs/$id"
            params={{ id: jobParam }}
            className="font-mono text-[12.5px] text-blue-700 hover:underline"
          >
            {row.servicePublicId ?? 'Open'}
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        )
      },
    },
    {
      key: 'message',
      label: 'Message',
      filter: 'text',
      // Match on the full message, not just the visible (elided) portion.
      filterValue: (row) => row.content,
      cellClass: 'text-slate-600',
      render: (row) => <MessageCell content={row.content} />,
    },
    {
      key: 'status',
      label: 'Status',
      widthClass: 'w-32',
      sortValue: (row) => row.status,
      filter: 'select',
      filterValue: (row) => row.status,
      filterOptions: (
        ['Queued', 'Held', 'Sent', 'Cancelled', 'Failed'] as const
      ).map((status) => ({
        value: status,
        label: status === 'Held' ? 'Disabled' : status,
      })),
      render: (row) => (
        <StatusCell
          row={row}
          sendingEnabled={opts.sendingEnabled}
          onCancelMessage={opts.onCancelMessage}
          onResendMessage={opts.onResendMessage}
        />
      ),
    },
    {
      key: 'sent',
      label: 'Sent',
      widthClass: 'w-28',
      sortValue: (row) => row.sortTimestamp,
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-[12.5px] text-slate-400',
      render: (row) => row.sentAtLabel || '—',
    },
  ]
}

export function WhatsAppScreen({
  rows,
  sendingEnabled = true,
  onCancelMessage,
  onResendMessage,
}: WhatsAppScreenProps) {
  const columns = buildColumns({
    sendingEnabled,
    onCancelMessage,
    onResendMessage,
  })

  return (
    <LedgerTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.id}
      itemLabelPlural="messages"
      emptyLabel="No WhatsApp messages yet."
      title="Messages"
      description="Messages sent to customers and messages waiting in the queue. Search and filter across the log."
      defaultSort={{ key: 'sent', dir: 'desc' }}
      initialPageSize={25}
      minTableWidthClass="min-w-[820px]"
    />
  )
}
